// covered-by: frontends/carrie/src/ask-v2/services/__tests__/chat.test.ts
import { z } from "zod";

import { API_URL } from "../../config/env";
import { smartFetch } from "../../utils/http";

const ackMessageSchema = z.object({
  _id: z.string(),
  content: z.string(),
});

const createChatAckSchema = z.object({
  chat: z.object({ _id: z.string() }),
  userMessage: ackMessageSchema,
  assistantMessage: ackMessageSchema.optional(),
});

const sendAckSchema = z.object({
  userMessage: ackMessageSchema,
  assistantMessage: ackMessageSchema.optional(),
});

const abortAckSchema = z.object({
  message: ackMessageSchema,
});

const regenerateAckSchema = z.object({
  userMessage: ackMessageSchema,
  assistantMessage: ackMessageSchema.optional(),
});

export type AskV2ChatAck = {
  chatId?: string;
  userMessage: { id: string; content: string };
  assistantMessage?: { id: string; content: string };
};

function toAck(
  userMessage: { _id: string; content: string },
  assistantMessage?: { _id: string; content: string },
  chatId?: string,
): AskV2ChatAck {
  return {
    chatId,
    userMessage: { id: userMessage._id, content: userMessage.content },
    assistantMessage: assistantMessage
      ? { id: assistantMessage._id, content: assistantMessage.content }
      : undefined,
  };
}

const chatsUrl = `${API_URL}/private/catalyst/ask-puntt/chats`;

export async function createAskV2Chat(message: string): Promise<AskV2ChatAck> {
  const json = await smartFetch(chatsUrl, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
  const parsed = createChatAckSchema.parse(json);
  return toAck(parsed.userMessage, parsed.assistantMessage, parsed.chat._id);
}

export async function sendAskV2Message(
  chatId: string,
  message: string,
): Promise<AskV2ChatAck> {
  const json = await smartFetch(`${chatsUrl}/${chatId}/messages`, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
  const parsed = sendAckSchema.parse(json);
  return toAck(parsed.userMessage, parsed.assistantMessage, chatId);
}

export async function abortAskV2Message(
  chatId: string,
  messageId: string,
): Promise<void> {
  const json = await smartFetch(
    `${chatsUrl}/${chatId}/messages/${messageId}/abort`,
    { method: "POST" },
  );
  abortAckSchema.parse(json);
}

export async function regenerateAskV2Message(
  chatId: string,
  messageId: string,
  content: string,
): Promise<AskV2ChatAck> {
  const json = await smartFetch(
    `${chatsUrl}/${chatId}/messages/${messageId}/regenerate`,
    {
      method: "POST",
      body: JSON.stringify({ content }),
    },
  );
  const parsed = regenerateAckSchema.parse(json);
  return toAck(parsed.userMessage, parsed.assistantMessage, chatId);
}
